// src/components/DocumentExplorer.js
// File Explorer-style document management - real nested folders, double-click
// navigation, breadcrumb, self-service folder management, drag-and-drop.
//
// mode='case' (default): one matter's folder tree - unchanged entry point
//   used by CaseDetailsPage.js.
// mode='client': every one of a client's matters as top-level folder nodes,
//   plus a virtual "Unfiled" bucket for documents with no case - used by
//   ClientManagement.js's Documents tab.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import 'animate.css';
import SendPdfButton from './shared/SendPdfButton';
import { useConfirm } from './ConfirmProvider';
import FolderTree from './document-explorer/FolderTree';
import FolderBreadcrumb from './document-explorer/FolderBreadcrumb';

function normalizeFolderNode(folder) {
  const children = (folder.children || []).map(normalizeFolderNode);
  return {
    id: `folder-${folder.id}`,
    type: 'folder',
    name: folder.name,
    isVirtual: false,
    raw: folder,
    caseId: folder.case,
    children,
    hasChildren: children.length > 0,
  };
}

function findNodePath(targetId, nodes, trail) {
  for (const n of nodes) {
    const nextTrail = [...trail, n];
    if (n.id === targetId) return nextTrail;
    if (n.children && n.children.length) {
      const found = findNodePath(targetId, n.children, nextTrail);
      if (found) return found;
    }
  }
  return null;
}

function findNodeById(targetId, nodes) {
  for (const n of nodes) {
    if (n.id === targetId) return n;
    if (n.children && n.children.length) {
      const found = findNodeById(targetId, n.children);
      if (found) return found;
    }
  }
  return null;
}

// Small local prompt-style modal for New Folder / Rename, matching this
// codebase's small-centered-modal convention rather than window.prompt().
function PromptModal({ title, label, initialValue = '', onSubmit, onCancel }) {
  const [value, setValue] = useState(initialValue);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
        <div className="px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-800">{title}</h3>
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); if (value.trim()) onSubmit(value.trim()); }}
          className="p-5 space-y-3"
        >
          <label className="block text-xs font-medium text-gray-700">{label}</label>
          <input
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded">
              Cancel
            </button>
            <button
              type="submit"
              disabled={!value.trim()}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Small hover-only rename/delete affordance shown on real (non-virtual)
// folder tiles in the right pane.
function FolderTileActions({ onRename, onDelete }) {
  return (
    <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onRename(); }}
        title="Rename"
        className="w-6 h-6 flex items-center justify-center bg-white border border-gray-300 rounded shadow-sm text-gray-500 hover:text-blue-600 hover:border-blue-300"
      >
        <i className="bi bi-pencil" style={{ fontSize: '0.7rem' }}></i>
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
        title="Delete"
        className="w-6 h-6 flex items-center justify-center bg-white border border-gray-300 rounded shadow-sm text-gray-500 hover:text-red-600 hover:border-red-300"
      >
        <i className="bi bi-trash" style={{ fontSize: '0.7rem' }}></i>
      </button>
    </div>
  );
}

const DocumentExplorer = ({ authAxios, onToast, mode = 'case', caseId, clientId, clientName }) => {
  const confirm = useConfirm();

  // Case mode: this case's real folder tree + the legacy document-type
  // virtual folders (kept alongside real folders per this codebase's
  // existing convention for documents uploaded without a real folder).
  const [serverFolderTree, setServerFolderTree] = useState([]);
  const [caseDocTypes, setCaseDocTypes] = useState([]);
  const [caseInfo, setCaseInfo] = useState(null);

  // Client mode: every matter (Case) for this client, each with its own
  // real folder tree, plus documents with no case at all ("Unfiled").
  const [caseList, setCaseList] = useState([]);
  const [caseTrees, setCaseTrees] = useState({});
  const [unfiledClientDocs, setUnfiledClientDocs] = useState([]);

  const [documents, setDocuments] = useState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [showMetadataPanel, setShowMetadataPanel] = useState(false);
  const [expandedVersions, setExpandedVersions] = useState({});
  const [promptModal, setPromptModal] = useState(null); // {mode: 'new'|'rename', node?}
  const [selectedDocCaseInfo, setSelectedDocCaseInfo] = useState(null);

  // Reset navigation whenever the target case/client changes, so a folder
  // id from a previously-viewed matter never carries over.
  useEffect(() => {
    setSelectedNodeId(null);
    setExpandedIds(new Set());
  }, [mode, caseId, clientId]);

  // --- Case mode fetches ---
  const fetchCaseFolderTree = useCallback(async () => {
    if (mode !== 'case' || !caseId) return;
    try {
      const resp = await authAxios.get(`/documents/folders/?case=${caseId}&as_tree=true`);
      setServerFolderTree(Array.isArray(resp.data) ? resp.data : []);
    } catch (err) {
      console.error('Failed to fetch folder tree', err);
      onToast?.('Failed to load folders', 'error');
    }
  }, [authAxios, caseId, mode, onToast]);

  const fetchCaseDocTypes = useCallback(async () => {
    if (mode !== 'case' || !caseId) return;
    try {
      const resp = await authAxios.get('/documents/', { params: { case: caseId } });
      const data = resp.data?.results || resp.data || [];
      const docs = Array.isArray(data) ? data : [];
      setCaseDocTypes(Array.from(new Set(docs.map(d => d.document_type || 'Other'))));
    } catch (err) {
      console.error('Failed to fetch document types', err);
    }
  }, [authAxios, caseId, mode]);

  useEffect(() => {
    if (mode !== 'case') return;
    fetchCaseFolderTree();
    fetchCaseDocTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, caseId]);

  useEffect(() => {
    if (mode !== 'case' || !caseId) { setCaseInfo(null); return; }
    let cancelled = false;
    authAxios.get(`cases/${caseId}/`)
      .then(resp => { if (!cancelled) setCaseInfo(resp.data); })
      .catch(() => { if (!cancelled) setCaseInfo(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, caseId]);

  // --- Client mode fetch: compose per-matter trees + unfiled documents ---
  const fetchClientTree = useCallback(async () => {
    if (mode !== 'client' || !clientId) return;
    setLoading(true);
    try {
      const [casesResp, docsResp] = await Promise.all([
        authAxios.get('/cases/', { params: { client: clientId } }),
        authAxios.get('/documents/', { params: { client: clientId } }),
      ]);
      const allCases = casesResp.data?.results || casesResp.data || [];
      const cases = (Array.isArray(allCases) ? allCases : []).filter(c => c.status !== 'Archived');
      setCaseList(cases);

      const treeResults = await Promise.all(cases.map(async (c) => {
        try {
          const resp = await authAxios.get(`/documents/folders/?case=${c.id}&as_tree=true`);
          return [c.id, Array.isArray(resp.data) ? resp.data : []];
        } catch (err) {
          console.error(`Failed to fetch folder tree for case ${c.id}`, err);
          return [c.id, []];
        }
      }));
      setCaseTrees(Object.fromEntries(treeResults));

      const allDocs = docsResp.data?.results || docsResp.data || [];
      setUnfiledClientDocs((Array.isArray(allDocs) ? allDocs : []).filter(d => !d.case));
    } catch (err) {
      console.error('Failed to load client document tree', err);
      onToast?.('Failed to load documents', 'error');
    } finally {
      setLoading(false);
    }
  }, [authAxios, clientId, mode, onToast]);

  useEffect(() => {
    if (mode !== 'client') return;
    fetchClientTree();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, clientId]);

  // --- Compose top-level tree nodes for the active mode ---
  const treeNodes = useMemo(() => {
    if (mode === 'client') {
      const matterNodes = caseList.map(c => {
        const rawTree = caseTrees[c.id] || [];
        const children = rawTree.map(normalizeFolderNode);
        return {
          id: `matter-${c.id}`, type: 'matter', name: c.title || `Matter #${c.id}`,
          isVirtual: true, raw: c, caseId: c.id, children, hasChildren: children.length > 0,
        };
      });
      const unfiledNode = {
        id: 'client-unfiled', type: 'unfiled', name: 'Unfiled', isVirtual: true,
        raw: null, caseId: null, children: [], hasChildren: false,
      };
      return [...matterNodes, unfiledNode];
    }
    // Case mode: legacy document-type pseudo-folders first, then the real tree.
    const allTypesNode = {
      id: 'type-All', type: 'doctype', name: 'All Types', isVirtual: true,
      raw: null, docType: null, caseId, children: [], hasChildren: false,
    };
    const typeNodes = caseDocTypes.map(t => ({
      id: `type-${t}`, type: 'doctype', name: t, isVirtual: true,
      raw: null, docType: t, caseId, children: [], hasChildren: false,
    }));
    const realNodes = serverFolderTree.map(normalizeFolderNode);
    return [allTypesNode, ...typeNodes, ...realNodes];
  }, [mode, caseList, caseTrees, caseDocTypes, serverFolderTree, caseId]);

  // Synthetic root wrapping treeNodes as its own children - lets the browser
  // "start" somewhere sensible (a matter's own root, or a client's list of
  // matters) without a real DocumentFolder backing it.
  const rootNode = useMemo(() => ({
    id: mode === 'client' ? 'client-root' : 'case-root',
    type: mode === 'client' ? 'client' : 'matter',
    name: mode === 'client' ? (clientName || 'Client') : (caseInfo?.title || 'Matter'),
    isVirtual: true, raw: null,
    caseId: mode === 'case' ? caseId : null,
    children: treeNodes, hasChildren: treeNodes.length > 0,
  }), [mode, clientName, caseInfo, caseId, treeNodes]);

  const selectedFolder = useMemo(() => {
    if (!selectedNodeId || selectedNodeId === rootNode.id) return rootNode;
    return findNodeById(selectedNodeId, rootNode.children) || rootNode;
  }, [selectedNodeId, rootNode]);

  const currentPath = useMemo(() => {
    if (!selectedNodeId || selectedNodeId === rootNode.id) return [rootNode];
    return findNodePath(selectedNodeId, rootNode.children, [rootNode]) || [rootNode];
  }, [selectedNodeId, rootNode]);

  const subfolders = selectedFolder.children || [];
  const canCreateFolderHere = selectedFolder.type === 'folder' || selectedFolder.type === 'matter';

  // --- Navigation ---
  const handleOpenFolder = useCallback((node) => setSelectedNodeId(node.id), []);

  const handleGoUp = useCallback(() => {
    if (currentPath.length <= 1) return;
    setSelectedNodeId(currentPath[currentPath.length - 2].id);
  }, [currentPath]);

  const handleBreadcrumbNavigate = useCallback((index) => {
    setSelectedNodeId(currentPath[index].id);
  }, [currentPath]);

  const handleToggleExpand = useCallback((nodeId) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId); else next.add(nodeId);
      return next;
    });
  }, []);

  // --- Fetch the currently open folder's documents (right pane content) ---
  const fetchDocuments = useCallback(async () => {
    if (selectedFolder.type === 'unfiled') {
      setDocuments(unfiledClientDocs);
      return;
    }
    if (selectedFolder.type === 'client') {
      setDocuments([]);
      return;
    }
    let params;
    if (selectedFolder.type === 'doctype') {
      params = selectedFolder.docType ? { case: caseId, document_type: selectedFolder.docType } : { case: caseId };
    } else if (selectedFolder.type === 'folder') {
      params = { case: selectedFolder.caseId, folder: selectedFolder.raw.id };
    } else if (selectedFolder.type === 'matter') {
      params = { case: selectedFolder.caseId, folder: 'none' };
    } else {
      setDocuments([]);
      return;
    }
    setLoading(true);
    try {
      const resp = await authAxios.get('/documents/', { params });
      const data = resp.data?.results || resp.data || [];
      setDocuments(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch documents', err);
      onToast?.('Failed to load documents', 'error');
    } finally {
      setLoading(false);
    }
  }, [authAxios, selectedFolder, caseId, unfiledClientDocs, onToast]);

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFolder.id, selectedFolder.type, unfiledClientDocs]);

  // Certified-copy email prefill needs the OWNING case of the selected
  // document, not the component's own caseId prop (undefined in client mode).
  useEffect(() => {
    if (!selectedDoc?.case) { setSelectedDocCaseInfo(null); return; }
    if (mode === 'case' && selectedDoc.case === caseId) { setSelectedDocCaseInfo(caseInfo); return; }
    let cancelled = false;
    authAxios.get(`cases/${selectedDoc.case}/`)
      .then(resp => { if (!cancelled) setSelectedDocCaseInfo(resp.data); })
      .catch(() => { if (!cancelled) setSelectedDocCaseInfo(null); });
    return () => { cancelled = true; };
  }, [selectedDoc?.case, mode, caseId, caseInfo, authAxios]);

  // --- Upload ---
  const handleFileUpload = useCallback(async (files) => {
    if (!files || files.length === 0) return;
    const targetCaseId = selectedFolder.caseId;
    if (!targetCaseId) {
      onToast?.('Select a matter or folder before uploading.', 'error');
      return;
    }
    setUploading(true);
    try {
      onToast?.(`Uploading ${files.length} file(s)...`, 'info');
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const formData = new FormData();
        formData.append('file', file);
        formData.append('case', targetCaseId);
        if (selectedFolder.type === 'folder') {
          formData.append('folder', selectedFolder.raw.id);
        } else if (selectedFolder.type === 'doctype' && selectedFolder.docType) {
          formData.append('document_type', selectedFolder.docType);
        }
        formData.append('title', file.name);
        await authAxios.post('/documents/', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      onToast?.('Files uploaded successfully', 'success');
      if (mode === 'client') fetchClientTree(); else { fetchDocuments(); fetchCaseDocTypes(); }
    } catch (err) {
      console.error('Upload failed', err);
      onToast?.('Upload failed', 'error');
    } finally {
      setUploading(false);
      setDragging(false);
    }
  }, [authAxios, selectedFolder, mode, fetchClientTree, fetchDocuments, fetchCaseDocTypes, onToast]);

  const handleDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setDragging(false); };
  const handleDrop = (e) => { e.preventDefault(); handleFileUpload(e.dataTransfer.files); };

  const moveDocumentToFolder = useCallback(async (docId, folderId) => {
    try {
      await authAxios.patch('/documents/move/', { document_id: docId, folder_id: folderId });
      onToast?.('Document moved', 'success');
      if (mode === 'client') fetchClientTree(); else fetchDocuments();
    } catch (err) {
      console.error('Failed to move document', err);
      onToast?.(err.response?.data?.detail || 'Failed to move document', 'error');
    }
  }, [authAxios, mode, fetchClientTree, fetchDocuments, onToast]);

  const changeDocumentType = useCallback(async (docId, docType) => {
    try {
      await authAxios.patch(`/documents/${docId}/`, { document_type: docType });
      onToast?.('Document type updated', 'success');
      fetchDocuments();
      fetchCaseDocTypes();
    } catch (err) {
      console.error('Failed to change document type', err);
      onToast?.('Failed to change document type', 'error');
    }
  }, [authAxios, fetchDocuments, fetchCaseDocTypes, onToast]);

  const handleDropDocumentOnTree = useCallback((docId, targetNode) => {
    if (targetNode.type === 'doctype') return changeDocumentType(docId, targetNode.docType || 'Other');
    if (targetNode.type === 'folder') return moveDocumentToFolder(docId, targetNode.raw.id);
    if (targetNode.type === 'matter') {
      const doc = documents.find(d => String(d.id) === String(docId))
        || unfiledClientDocs.find(d => String(d.id) === String(docId));
      if (doc && doc.case && doc.case !== targetNode.caseId) {
        onToast?.('Move within the same matter, or open the document to change its case.', 'error');
        return undefined;
      }
      return moveDocumentToFolder(docId, null);
    }
    return undefined; // unfiled/client targets aren't valid document-drop targets
  }, [documents, unfiledClientDocs, changeDocumentType, moveDocumentToFolder, onToast]);

  // --- Folder CRUD ---
  const handleCreateFolder = useCallback(async (name) => {
    if (!canCreateFolderHere) return;
    const parentId = selectedFolder.type === 'folder' ? selectedFolder.raw.id : null;
    const targetCaseId = selectedFolder.caseId;
    try {
      await authAxios.post('/documents/folders/', { case: targetCaseId, parent: parentId, name });
      onToast?.('Folder created', 'success');
      setPromptModal(null);
      if (mode === 'client') fetchClientTree(); else fetchCaseFolderTree();
    } catch (err) {
      onToast?.(err.response?.data?.detail || 'Failed to create folder', 'error');
    }
  }, [authAxios, selectedFolder, canCreateFolderHere, mode, fetchClientTree, fetchCaseFolderTree, onToast]);

  const handleRenameFolder = useCallback(async (node, newName) => {
    try {
      await authAxios.patch('/documents/folders/rename/', { folder_id: node.raw.id, name: newName });
      onToast?.('Folder renamed', 'success');
      setPromptModal(null);
      if (mode === 'client') fetchClientTree(); else fetchCaseFolderTree();
    } catch (err) {
      onToast?.(err.response?.data?.detail || 'Failed to rename folder', 'error');
    }
  }, [authAxios, mode, fetchClientTree, fetchCaseFolderTree, onToast]);

  const handleDeleteFolder = useCallback(async (node) => {
    try {
      await authAxios.post('/documents/folders/delete/', { folder_id: node.raw.id });
      onToast?.('Folder deleted', 'success');
      if (mode === 'client') fetchClientTree(); else fetchCaseFolderTree();
    } catch (err) {
      onToast?.(err.response?.data?.detail || 'Folder is not empty', 'error');
    }
  }, [authAxios, mode, fetchClientTree, fetchCaseFolderTree, onToast]);

  const handleDeleteFolderConfirm = useCallback(async (node) => {
    const ok = await confirm({
      title: 'Delete folder',
      message: `Delete "${node.name}"? This only works if the folder is empty.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    if (ok) handleDeleteFolder(node);
  }, [confirm, handleDeleteFolder]);

  const handleDropFolderOnTree = useCallback(async (draggedFolderId, targetNode) => {
    const draggedNode = findNodeById(`folder-${draggedFolderId}`, rootNode.children);
    let newParentId;
    if (targetNode.type === 'folder') {
      newParentId = targetNode.raw.id;
    } else if (targetNode.type === 'matter') {
      if (!draggedNode || draggedNode.caseId !== targetNode.caseId) {
        onToast?.('Cannot move a folder into a different matter.', 'error');
        return;
      }
      newParentId = null;
    } else {
      return; // unfiled/doctype/client aren't valid folder-drop targets
    }
    try {
      const body = { folder_id: draggedFolderId };
      if (newParentId !== null) body.new_parent_id = newParentId;
      await authAxios.patch('/documents/folders/move/', body);
      onToast?.('Folder moved', 'success');
      if (mode === 'client') fetchClientTree(); else fetchCaseFolderTree();
    } catch (err) {
      onToast?.(err.response?.data?.detail || 'Failed to move folder', 'error');
    }
  }, [rootNode, mode, fetchClientTree, fetchCaseFolderTree, authAxios, onToast]);

  // --- Document actions ---
  const getVersionCount = (doc) => doc.versions?.length || 0;
  const getLatestVersionNumber = (doc) => {
    if (!doc.versions || doc.versions.length === 0) return 1;
    return Math.max(...doc.versions.map(v => v.version_number || 1));
  };
  const toggleVersions = (docId) => setExpandedVersions(prev => ({ ...prev, [docId]: !prev[docId] }));

  const handleVerifyDocument = async (documentId) => {
    try {
      await authAxios.post(`documents/${documentId}/verify/`);
      onToast?.('Document verified successfully!', 'success');
      fetchDocuments();
      if (selectedDoc?.id === documentId) setSelectedDoc({ ...selectedDoc, is_verified: true });
    } catch (error) {
      console.error('Error verifying document:', error);
      onToast?.(error.response?.data?.detail || 'Failed to verify document.', 'error');
    }
  };

  const handleCertifyDocument = async (documentId) => {
    try {
      const resp = await authAxios.post(`documents/${documentId}/certify/`);
      onToast?.('Document marked as a certified copy.', 'success');
      fetchDocuments();
      if (selectedDoc?.id === documentId) setSelectedDoc({ ...selectedDoc, ...resp.data });
    } catch (error) {
      console.error('Error certifying document:', error);
      onToast?.(error.response?.data?.detail || 'Failed to certify document.', 'error');
    }
  };

  const fetchCertifiedCopyPdf = useCallback(async () => {
    const resp = await authAxios.get(`documents/${selectedDoc.id}/`);
    const fileUrl = resp.data.file;
    if (!fileUrl) throw new Error('This document has no attached file to email.');
    const fileResp = await fetch(fileUrl);
    return fileResp.blob();
  }, [authAxios, selectedDoc]);

  const displayDocuments = useMemo(() => documents.map(doc => ({
    ...doc,
    showVersions: expandedVersions[doc.id] || false,
    versionCount: getVersionCount(doc),
    latestVersion: getLatestVersionNumber(doc),
  })), [documents, expandedVersions]);

  // --- Folder tile (grid) / row (list) shared drag handlers ---
  const folderDropHandlers = (node) => ({
    onDragOver: (e) => e.preventDefault(),
    onDrop: (e) => {
      e.preventDefault();
      const draggedFolderId = e.dataTransfer.getData('application/x-folder-id');
      if (draggedFolderId) { handleDropFolderOnTree(draggedFolderId, node); return; }
      const docId = e.dataTransfer.getData('text/plain');
      if (docId) handleDropDocumentOnTree(docId, node);
    },
  });

  const folderIconFor = (node) => {
    if (node.type === 'matter') return 'bi-folder2';
    if (node.type === 'unfiled') return 'bi-folder-x';
    return 'bi-folder-fill';
  };

  // Render folder + document grid (icon view)
  const renderDocumentGrid = () => (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-4">
      {subfolders.map(node => (
        <div key={node.id} className="relative group">
          <div
            draggable={node.type === 'folder'}
            onDragStart={(e) => { if (node.type === 'folder') e.dataTransfer.setData('application/x-folder-id', String(node.raw.id)); }}
            onDoubleClick={() => handleOpenFolder(node)}
            {...folderDropHandlers(node)}
            className="bg-white border border-gray-200 rounded-lg p-4 cursor-pointer hover:shadow-lg hover:border-blue-300 transition-all flex flex-col items-center gap-2"
          >
            <i className={`bi ${folderIconFor(node)} text-5xl text-yellow-500`}></i>
            <div className="text-sm font-medium text-gray-700 text-center truncate w-full">{node.name}</div>
          </div>
          {!node.isVirtual && (
            <FolderTileActions
              onRename={() => setPromptModal({ mode: 'rename', node })}
              onDelete={() => handleDeleteFolderConfirm(node)}
            />
          )}
        </div>
      ))}
      {displayDocuments.map(doc => (
        <div key={doc.id} className="relative group">
          <div
            draggable
            onDragStart={(e) => { e.dataTransfer.setData('text/plain', String(doc.id)); }}
            onClick={() => { setSelectedDoc(doc); setShowMetadataPanel(true); }}
            className="bg-white border border-gray-200 rounded-lg p-4 cursor-pointer hover:shadow-lg transition-all hover:border-blue-300"
          >
            <div className="flex flex-col items-center gap-2">
              <div className="text-4xl text-blue-500">
                {doc.file?.endsWith('.pdf') ? (
                  <i className="bi bi-file-pdf"></i>
                ) : doc.file?.endsWith('.docx') || doc.file?.endsWith('.doc') ? (
                  <i className="bi bi-file-word"></i>
                ) : (
                  <i className="bi bi-file-earmark"></i>
                )}
              </div>
              <div className="text-sm font-medium text-gray-700 text-center truncate w-full">
                {doc.title}
              </div>
              {doc.versionCount > 0 && (
                <div
                  onClick={(e) => { e.stopPropagation(); toggleVersions(doc.id); }}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full cursor-pointer hover:bg-blue-200"
                >
                  <i className="bi bi-clock-history"></i>
                  v{doc.latestVersion}
                  {doc.versionCount > 1 && ` (+${doc.versionCount - 1})`}
                </div>
              )}
            </div>
          </div>
          {doc.showVersions && doc.versions && doc.versions.length > 0 && (
            <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-2">
              <div className="text-xs font-semibold text-gray-600 mb-2">Version History</div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {doc.versions.map(ver => (
                  <div key={ver.id} className="flex items-center justify-between text-xs p-1 hover:bg-gray-50 rounded">
                    <span className="font-medium">v{ver.version_number || 1}</span>
                    <span className="text-gray-500">{new Date(ver.uploaded_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  // Render folder + document list
  const renderDocumentList = () => (
    <div className="p-4">
      <table className="min-w-full bg-white border border-gray-200 rounded-lg overflow-hidden">
        <thead className="bg-gray-100">
          <tr>
            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Name</th>
            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Type</th>
            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Version</th>
            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Uploaded</th>
            <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Actions</th>
          </tr>
        </thead>
        <tbody>
          {subfolders.map(node => (
            <tr
              key={node.id}
              className="border-t hover:bg-gray-50 cursor-pointer group"
              onDoubleClick={() => handleOpenFolder(node)}
              {...folderDropHandlers(node)}
              onDragOver={(e) => e.preventDefault()}
            >
              <td className="px-4 py-2 text-sm">
                <div className="flex items-center gap-2">
                  <i className={`bi ${folderIconFor(node)} text-yellow-500`}></i>
                  <span className="font-medium">{node.name}</span>
                </div>
              </td>
              <td className="px-4 py-2 text-sm text-gray-600">Folder</td>
              <td className="px-4 py-2 text-sm">-</td>
              <td className="px-4 py-2 text-sm text-gray-600">-</td>
              <td className="px-4 py-2 text-sm">
                {!node.isVirtual && (
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                    <button onClick={() => setPromptModal({ mode: 'rename', node })} className="text-gray-500 hover:text-blue-600" title="Rename">
                      <i className="bi bi-pencil"></i>
                    </button>
                    <button onClick={() => handleDeleteFolderConfirm(node)} className="text-gray-500 hover:text-red-600" title="Delete">
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                )}
              </td>
            </tr>
          ))}
          {displayDocuments.map(doc => (
            <React.Fragment key={doc.id}>
              <tr className="border-t hover:bg-gray-50">
                <td className="px-4 py-2 text-sm">
                  <div className="flex items-center gap-2">
                    <i className="bi bi-file-earmark text-blue-500"></i>
                    <span className="font-medium">{doc.title}</span>
                  </div>
                </td>
                <td className="px-4 py-2 text-sm text-gray-600">{doc.document_type || 'Other'}</td>
                <td className="px-4 py-2 text-sm">
                  {doc.versionCount > 0 && (
                    <button
                      onClick={() => toggleVersions(doc.id)}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full hover:bg-blue-200"
                    >
                      <i className="bi bi-clock-history"></i>
                      v{doc.latestVersion}
                      {doc.versionCount > 1 && ` (+${doc.versionCount - 1})`}
                    </button>
                  )}
                </td>
                <td className="px-4 py-2 text-sm text-gray-600">
                  {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString() : 'N/A'}
                </td>
                <td className="px-4 py-2 text-sm">
                  <button
                    onClick={() => { setSelectedDoc(doc); setShowMetadataPanel(true); }}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <i className="bi bi-info-circle"></i>
                  </button>
                </td>
              </tr>
              {doc.showVersions && doc.versions && doc.versions.map(ver => (
                <tr key={`${doc.id}-v${ver.id}`} className="border-t bg-blue-50">
                  <td className="px-4 py-2 text-sm pl-12">
                    <span className="text-gray-600">└ Version {ver.version_number || 1}</span>
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-600">-</td>
                  <td className="px-4 py-2 text-sm text-gray-600">-</td>
                  <td className="px-4 py-2 text-sm text-gray-600">
                    {new Date(ver.uploaded_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2 text-sm">
                    {ver.file && (
                      <a href={ver.file} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                        <i className="bi bi-download"></i>
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );

  // Render metadata panel (right sidebar)
  const renderMetadataPanel = () => {
    if (!showMetadataPanel || !selectedDoc) return null;

    return (
      <div className="w-80 bg-white border-l border-gray-200 overflow-y-auto flex flex-col">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-gray-700">Document Details</h3>
          <button onClick={() => setShowMetadataPanel(false)} className="text-gray-500 hover:text-gray-700">
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
        <div className="p-4 space-y-4 flex-1">
          <div className="bg-gray-100 rounded-lg p-4 text-center">
            <i className="bi bi-file-earmark-text text-4xl text-gray-400"></i>
            <div className="text-sm text-gray-600 mt-2">Preview unavailable</div>
          </div>

          <div className="space-y-2">
            <div>
              <div className="text-xs text-gray-500 uppercase">Title</div>
              <div className="text-sm font-medium">{selectedDoc.title}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase">Type</div>
              <div className="text-sm">{selectedDoc.document_type || 'Other'}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase">Uploaded</div>
              <div className="text-sm">
                {selectedDoc.uploaded_at ? new Date(selectedDoc.uploaded_at).toLocaleString() : 'N/A'}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase">Uploaded By</div>
              <div className="text-sm">{selectedDoc.uploaded_by_name || 'Unknown'}</div>
            </div>
            {selectedDoc.description && (
              <div>
                <div className="text-xs text-gray-500 uppercase">Description</div>
                <div className="text-sm">{selectedDoc.description}</div>
              </div>
            )}
          </div>

          <div className="pt-4 border-t border-gray-200 space-y-2">
            {selectedDoc.file && (
              <a
                href={selectedDoc.file}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                <i className="bi bi-download"></i>
                Download
              </a>
            )}
            {selectedDoc.is_verified ? (
              <div className="flex items-center gap-2 text-green-600 text-sm">
                <i className="bi bi-check-circle-fill"></i>
                Verified
              </div>
            ) : (
              <button
                onClick={() => handleVerifyDocument(selectedDoc.id)}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 border border-green-600 text-green-600 rounded hover:bg-green-50 transition-colors"
              >
                <i className="bi bi-shield-check"></i>
                Verify Document
              </button>
            )}
            {selectedDoc.is_certified ? (
              <div className="flex items-center gap-2 text-blue-600 text-sm">
                <i className="bi bi-patch-check-fill"></i>
                Certified copy (by {selectedDoc.certified_by_username || 'unknown'})
              </div>
            ) : (
              <button
                onClick={() => handleCertifyDocument(selectedDoc.id)}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 border border-blue-600 text-blue-600 rounded hover:bg-blue-50 transition-colors"
              >
                <i className="bi bi-patch-check"></i>
                Mark as Certified Copy
              </button>
            )}
            {selectedDoc.is_certified && selectedDoc.file && (
              <SendPdfButton
                authAxios={authAxios}
                showFormMessage={(type, msg) => onToast?.(msg, type)}
                fetchPdf={fetchCertifiedCopyPdf}
                defaultFilename={selectedDoc.title || 'certified-copy.pdf'}
                defaultSubject={`Certified copy: ${selectedDoc.title || 'Document'}${selectedDocCaseInfo?.title ? ` — ${selectedDocCaseInfo.title}` : ''}`}
                defaultBody={`Dear Client,\n\nPlease find attached a certified copy of "${selectedDoc.title || 'the document'}"${selectedDocCaseInfo?.title ? ` in respect of ${selectedDocCaseInfo.title}` : ''}.\n\nRegards,\nLegalOps`}
                defaultRecipients={selectedDocCaseInfo?.client_email ? [selectedDocCaseInfo.client_email] : []}
                documentKind="CERTIFIED_DOCUMENT"
                objectId={selectedDoc.id}
                caseId={selectedDoc.case}
                buttonLabel="Email Certified Copy"
                buttonClassName="w-full inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors text-sm"
              />
            )}
          </div>

          {selectedDoc.versions && selectedDoc.versions.length > 0 && (
            <div className="pt-4 border-t border-gray-200">
              <div className="text-sm font-semibold text-gray-700 mb-2">
                Version History ({selectedDoc.versions.length})
              </div>
              <div className="space-y-2">
                {selectedDoc.versions.map(ver => (
                  <div key={ver.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div>
                      <div className="text-sm font-medium">Version {ver.version_number || 1}</div>
                      <div className="text-xs text-gray-500">{new Date(ver.uploaded_at).toLocaleDateString()}</div>
                    </div>
                    {ver.file && (
                      <a href={ver.file} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                        <i className="bi bi-download"></i>
                      </a>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (mode === 'case' && !caseId) {
    return <div className="p-8 text-center text-gray-500">Please select a case to view documents</div>;
  }
  if (mode === 'client' && !clientId) {
    return <div className="p-8 text-center text-gray-500">Please select a client to view documents</div>;
  }

  return (
    <div className="h-full flex flex-col">
      {promptModal && (
        <PromptModal
          title={promptModal.mode === 'new' ? 'New Folder' : 'Rename Folder'}
          label="Folder name"
          initialValue={promptModal.mode === 'rename' ? promptModal.node.name : ''}
          onCancel={() => setPromptModal(null)}
          onSubmit={(value) => {
            if (promptModal.mode === 'new') handleCreateFolder(value);
            else handleRenameFolder(promptModal.node, value);
          }}
        />
      )}

      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold text-gray-800">
            {mode === 'client' ? (clientName || 'Documents') : 'Document Explorer'}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleGoUp}
            disabled={currentPath.length <= 1}
            title="Up one level"
            className="px-3 py-1.5 border border-gray-300 rounded text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <i className="bi bi-arrow-up"></i>
          </button>
          <button
            onClick={() => setPromptModal({ mode: 'new' })}
            disabled={!canCreateFolderHere}
            title={canCreateFolderHere ? 'New Folder' : 'Select a matter or folder first'}
            className="px-3 py-1.5 border border-gray-300 rounded text-gray-700 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <i className="bi bi-folder-plus mr-1"></i>
            New Folder
          </button>
          <div className="flex border border-gray-300 rounded">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-1 text-sm ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <i className="bi bi-grid-3x3-gap"></i>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-1 text-sm border-l border-gray-300 ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <i className="bi bi-list-ul"></i>
            </button>
          </div>
          <label className="px-4 py-2 bg-blue-600 text-white rounded cursor-pointer hover:bg-blue-700 transition-colors">
            <i className="bi bi-upload mr-2"></i>
            Upload
            <input type="file" multiple className="hidden" onChange={(e) => handleFileUpload(e.target.files)} disabled={uploading} />
          </label>
        </div>
      </div>

      <FolderBreadcrumb path={currentPath} onNavigate={handleBreadcrumbNavigate} />

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden">
        <div className="w-64 flex-shrink-0">
          <FolderTree
            nodes={treeNodes}
            selectedFolderId={selectedFolder.id}
            expandedIds={expandedIds}
            onToggleExpand={handleToggleExpand}
            onOpenFolder={handleOpenFolder}
            onDropDocument={handleDropDocumentOnTree}
            onDropFolder={handleDropFolderOnTree}
          />
        </div>

        <div
          className="flex-1 overflow-y-auto relative"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {dragging && (
            <div className="absolute inset-0 bg-blue-100 bg-opacity-50 border-4 border-dashed border-blue-400 flex items-center justify-center z-10">
              <div className="text-center">
                <i className="bi bi-cloud-upload text-6xl text-blue-600"></i>
                <div className="text-xl font-semibold text-blue-700 mt-2">Drop files here</div>
              </div>
            </div>
          )}
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-gray-500">Loading...</div>
            </div>
          ) : subfolders.length === 0 && documents.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-500">
                <i className="bi bi-folder2-open text-6xl mb-4"></i>
                <div>Nothing here yet</div>
                <div className="text-sm mt-2">Drop files here or click Upload</div>
              </div>
            </div>
          ) : viewMode === 'grid' ? (
            renderDocumentGrid()
          ) : (
            renderDocumentList()
          )}
        </div>

        {renderMetadataPanel()}
      </div>
    </div>
  );
};

export default DocumentExplorer;
